import React from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { pageTitle } from '@/lib/seo';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { InlineVoteButton } from '@/components/InlineVoteButton';
import { CommentForm } from '@/components/CommentForm';
import { Header } from '@/components/Header';
import { PostDetailsSidebar, PostDetailsSidebarSkeleton } from '@/components/PostDetailsSidebar';
import { EditPostModal } from '@/components/EditPostModal';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { ImageLightbox } from '@/components/ImageLightbox';
import { useAuth } from '@/contexts/AuthContext';
import { useKeyboardShortcuts, formatShortcut } from '@/hooks/useKeyboardShortcuts';
import { getUserDisplayName, getUserInitial, getUserAvatarUrl } from '@/lib/utils';
import { MarkdownContent } from '@/components/MarkdownContent';
import { AdminUserLink } from '@/components/AdminUserLink';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { S3Image } from '@/components/ui/s3-image';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronDown,
  MessageSquare,
  CheckCircle,
  Trash2,
  MoreHorizontal,
  Link2,
  Pencil,
} from 'lucide-react';

interface MergedPostData {
  id: string;
  title: string;
  details?: string;
  created: string;
  author: {
    id?: string;
    name?: string;
    alias?: string;
    avatarURL?: string;
  };
  comments?: Array<{
    id: string;
    value: string;
    created: string;
    author: {
      id?: string;
      name?: string;
      alias?: string;
      avatarURL?: string;
    };
  }>;
}

interface Comment {
  id: string;
  value: string;
  created: string;
  imageURLs?: string[];
  author: {
    id: string;
    name: string;
    alias?: string;
    avatarURL?: string;
    isAdmin: boolean;
    isMigrated?: boolean;
  };
  replies?: Comment[];
  isStatusChange?: boolean;
  statusChangeValue?: string;
  mergedPostData?: MergedPostData;
}

interface Post {
  id: string;
  slug: string;
  title: string;
  details?: string;
  status: string;
  score: number;
  commentCount: number;
  imageURLs?: string[];
  created: string;
  statusChangedAt?: string;
  estimatedCompletionDate?: string;
  author: {
    id: string;
    name: string;
    alias?: string;
    avatarURL?: string;
    isMigrated?: boolean;
  };
  board: {
    id: string;
    name: string;
    slug: string;
  };
  category?: {
    id: string;
    name: string;
    color?: string;
  };
  _count?: {
    votes: number;
    comments: number;
  };
}

const statusConfig: Record<
  string,
  {
    label: string;
    variant: 'open' | 'review' | 'planned' | 'progress' | 'complete' | 'closed';
  }
> = {
  open: { label: 'Open', variant: 'open' },
  under_review: { label: 'Under Review', variant: 'review' },
  planned: { label: 'Planned', variant: 'planned' },
  in_progress: { label: 'In Progress', variant: 'progress' },
  completed: { label: 'Completed', variant: 'complete' },
  closed: { label: 'Closed', variant: 'closed' },
};

function TimeWithTooltip({ date, className }: { date: string; className?: string }) {
  const dateObj = new Date(date);
  const relativeTime = formatDistanceToNow(dateObj, { addSuffix: true });
  const exactTime = format(dateObj, "MMM d, yyyy 'at' h:mm a");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{relativeTime}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {exactTime}
      </TooltipContent>
    </Tooltip>
  );
}

const CommentItem = React.memo(function CommentItem({
  comment,
  postId,
  currentUserId,
  isAdmin,
  onImageClick,
  onDelete,
}: {
  comment: Comment;
  postId: string;
  currentUserId?: string;
  isAdmin?: boolean;
  onImageClick?: (images: string[], index: number) => void;
  onDelete?: (commentId: string) => void;
}) {
  const [isReplying, setIsReplying] = React.useState(false);

  if (comment.isStatusChange && comment.statusChangeValue) {
    const statusLabels: Record<string, string> = {
      open: 'Open',
      under_review: 'Under Review',
      planned: 'Planned',
      in_progress: 'In Progress',
      completed: 'Completed',
      closed: 'Closed',
    };

    return (
      <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
        <CheckCircle className="h-4 w-4" aria-hidden="true" />
        <span>
          <AdminUserLink userId={comment.author.id} className="font-medium text-foreground">
            {getUserDisplayName(comment.author)}
          </AdminUserLink>
          {' changed status to '}
          <span className="font-medium text-foreground">
            {statusLabels[comment.statusChangeValue] || comment.statusChangeValue}
          </span>
        </span>
        <TimeWithTooltip date={comment.created} className="text-xs" />
      </div>
    );
  }

  // Merged post comment - render as a widget
  if (comment.mergedPostData) {
    const merged = comment.mergedPostData;
    const mergedAuthorName = merged.author?.name || merged.author?.alias || 'Unknown';
    const mergedDate = new Date(merged.created).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    return (
      <div className="group py-3">
        <div className="flex gap-3">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage
              src={getUserAvatarUrl(comment.author) || undefined}
              alt={getUserDisplayName(comment.author)}
            />
            <AvatarFallback className="text-xs" colorSeed={comment.author.id}>{getUserInitial(comment.author)}</AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <AdminUserLink userId={comment.author.id} className="font-medium text-sm">
                {getUserDisplayName(comment.author)}
              </AdminUserLink>
              {comment.author.isAdmin && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                >
                  Team
                </Badge>
              )}
              <TimeWithTooltip
                date={comment.created}
                className="text-xs text-muted-foreground"
              />
            </div>

            <p className="text-sm text-muted-foreground mb-2">Merged in a post:</p>

            {/* Merged post card */}
            <div className="border border-border rounded-lg p-4 bg-muted/30">
              <h4 className="font-semibold text-base mb-2">{merged.title}</h4>

              <div className="flex items-center gap-2 mb-2">
                <Avatar className="h-5 w-5">
                  <AvatarImage src={merged.author?.avatarURL || undefined} />
                  <AvatarFallback className="text-[10px]" colorSeed={merged.author?.id || merged.author?.name || 'unknown'}>
                    {(merged.author?.name || merged.author?.alias || 'U')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {merged.author?.id ? (
                  <AdminUserLink userId={merged.author.id} className="text-sm font-medium">
                    {mergedAuthorName}
                  </AdminUserLink>
                ) : (
                  <span className="text-sm font-medium">{mergedAuthorName}</span>
                )}
              </div>

              {merged.details && (
                <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">
                  {merged.details}
                </p>
              )}

              <p className="text-xs text-muted-foreground">{mergedDate}</p>

              {/* Comments from merged post */}
              {merged.comments && merged.comments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Comments ({merged.comments.length}):
                  </p>
                  <div className="space-y-2">
                    {merged.comments.map((c) => {
                      const commentAuthor = c.author?.name || c.author?.alias || 'Unknown';
                      const commentDate = new Date(c.created).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      });
                      return (
                        <div key={c.id} className="text-sm">
                          {c.author?.id ? (
                            <AdminUserLink userId={c.author.id} className="font-medium">
                              {commentAuthor}
                            </AdminUserLink>
                          ) : (
                            <span className="font-medium">{commentAuthor}</span>
                          )}
                          <span className="text-muted-foreground"> ({commentDate}): </span>
                          <span className="text-muted-foreground">{c.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group py-3">
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage
            src={getUserAvatarUrl(comment.author) || undefined}
            alt={getUserDisplayName(comment.author)}
          />
          <AvatarFallback className="text-xs" colorSeed={comment.author.id}>{getUserInitial(comment.author)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AdminUserLink userId={comment.author.id} className="font-medium text-sm">
              {getUserDisplayName(comment.author)}
            </AdminUserLink>
            {comment.author.isAdmin && (
              <Badge variant="default" className="text-[10px] px-1.5 py-0">
                Admin
              </Badge>
            )}
            <TimeWithTooltip
              date={comment.created}
              className="text-xs text-muted-foreground"
            />
          </div>

          <div className="text-sm text-foreground">
            <MarkdownContent content={comment.value} />
          </div>

          {comment.imageURLs && comment.imageURLs.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {comment.imageURLs.map((url, index) => (
                <S3Image
                  key={index}
                  src={url}
                  alt={`Comment image ${index + 1}`}
                  className="rounded border max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => onImageClick?.(comment.imageURLs!, index)}
                />
              ))}
            </div>
          )}

          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => setIsReplying(!isReplying)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {isReplying ? 'Cancel' : 'Reply'}
            </button>
            {isAdmin && onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-0.5 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                    aria-label="Comment actions"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => onDelete(comment.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isReplying && (
            <div className="mt-3 pl-3 border-l-2 border-muted">
              <CommentForm
                postId={postId}
                parentId={comment.id}
                placeholder="Write a reply..."
                autoFocus
                onSuccess={() => setIsReplying(false)}
                onCancel={() => setIsReplying(false)}
              />
            </div>
          )}

          {comment.replies && comment.replies.length > 0 && (
            <div className="ml-4 mt-3 space-y-0 border-l-2 border-muted pl-3">
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onImageClick={onImageClick}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

function PostDetailSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle('Loading post…')}</title>
      </Helmet>
      <Header />
      <div className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-8">
          <div className="flex-1 min-w-0">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-16 rounded-md" />
                <Skeleton className="h-7 w-2/3" />
              </div>
              <Skeleton className="h-4 w-24" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="space-y-2 pt-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>

            <div className="mt-8">
              <Skeleton className="h-5 w-28 mb-4" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
          <aside className="w-64 shrink-0 hidden lg:block">
            <PostDetailsSidebarSkeleton />
          </aside>
        </div>
      </main>
    </div>
  );
}

export default function PostDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const postSlug = params.slug as string;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [lightboxImages, setLightboxImages] = React.useState<string[]>([]);
  const [mobileDetailsOpen, setMobileDetailsOpen] = React.useState(false);
  const [commentSort, setCommentSort] = React.useState<'newest' | 'oldest'>('newest');
  const [commentToDelete, setCommentToDelete] = React.useState<string | null>(null);

  const { data: postData, isLoading: postLoading } = useQuery({
    queryKey: ['post', postSlug],
    queryFn: () => api.posts.get(postSlug),
    placeholderData: keepPreviousData,
  });

  const postId = postData?.post?.id;

  const { data: commentsData, isLoading: commentsLoading } = useQuery({
    queryKey: ['comments', postId],
    queryFn: () => api.comments.list(postId!),
    enabled: !!postId,
    placeholderData: keepPreviousData,
  });

  const boardSlug = postData?.post?.board?.slug;
  const { data: boardData } = useQuery({
    queryKey: ['board', boardSlug],
    queryFn: () => api.boards.get(boardSlug!),
    enabled: !!boardSlug && isEditModalOpen,
  });

  const deletePostMutation = useMutation({
    mutationFn: () => api.posts.delete(postSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success('Post deleted');
      navigate('/boards');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.comments.delete(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['post', postSlug] });
      toast.success('Comment deleted');
      setCommentToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete comment');
    },
  });

  const handleDelete = () => {
    setIsDeleting(true);
    deletePostMutation.mutate();
  };

  const handleDeleteComment = () => {
    if (commentToDelete) {
      deleteCommentMutation.mutate(commentToDelete);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const focusCommentForm = React.useCallback(() => {
    const textarea = document.querySelector<HTMLTextAreaElement>(
      '[data-comment-form] textarea, [data-comment-form] [contenteditable]'
    );
    if (textarea) {
      textarea.focus();
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'c',
        handler: focusCommentForm,
        description: 'Focus comment input',
      },
      {
        key: 'c',
        meta: true,
        shift: true,
        handler: handleCopyLink,
        description: 'Copy link',
      },
      {
        key: 'e',
        handler: () => {
          if (user && (user.id === postData?.post?.author?.id || user.isAdmin)) {
            setIsEditModalOpen(true);
          }
        },
        description: 'Edit post',
      },
    ],
    enabled: !lightboxOpen && !isEditModalOpen && !isDeleteDialogOpen,
  });

  const comments = React.useMemo<Comment[]>(
    () => commentsData?.comments || [],
    [commentsData?.comments]
  );

  const sortedComments = React.useMemo(() => {
    const sorted = [...comments];
    if (commentSort === 'newest') {
      sorted.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    } else {
      sorted.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
    }
    return sorted;
  }, [comments, commentSort]);

  const post: Post | undefined = postData?.post;

  // Only show skeleton when we have no post data at all
  if (!post && (postLoading || commentsLoading)) {
    return <PostDetailSkeleton />;
  }
  const hasVoted: boolean =
    (postData as { post: Post; hasVoted?: boolean })?.hasVoted || false;

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{pageTitle('Post not found')}</title>
        </Helmet>
        <Header />
        <div className="flex items-center justify-center py-24">
          <Card className="text-center p-8">
            <CardContent>
              <p className="text-xl font-semibold mb-4">Post not found</p>
              <Button asChild variant="outline">
                <Link to="/boards">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Boards
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const status = statusConfig[post.status] || statusConfig.open;
  const canEdit = user && (user.id === post.author.id || user.isAdmin);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{pageTitle(post.title)}</title>
        </Helmet>
        <Header />

        {/* Simple header with breadcrumb */}
        <div className="border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <Link
              to={`/boards/${post.board.slug}`}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              {post.board.name}
            </Link>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-8">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Post Block */}
              <div className="bg-card border border-border rounded-xl p-5 mb-6">
                <div className="flex gap-4">
                  {/* Left column: Vote button */}
                  <div className="shrink-0">
                    <InlineVoteButton
                      postId={post.id}
                      initialScore={post.score}
                      initialVoted={hasVoted}
                      disabled={user?.id === post.author.id}
                    />
                  </div>

                  {/* Right column: Title, Author, Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title row with kebab menu */}
                    <div className="flex items-start justify-between gap-3">
                      <h1 className="text-2xl font-semibold leading-tight">{post.title}</h1>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 -mt-1">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Post actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleCopyLink}>
                            <Link2 className="mr-2 h-4 w-4" />
                            Copy link
                            <DropdownMenuShortcut>
                              {formatShortcut({ key: 'c', meta: true, shift: true, handler: () => {} })}
                            </DropdownMenuShortcut>
                          </DropdownMenuItem>
                          {canEdit && (
                            <>
                              <DropdownMenuItem onClick={() => setIsEditModalOpen(true)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit post
                                <DropdownMenuShortcut>E</DropdownMenuShortcut>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setIsDeleteDialogOpen(true)}
                                disabled={isDeleting}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete post
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Author row */}
                    <div className="flex items-center gap-2 mt-3">
                      <Avatar className="h-6 w-6">
                        <AvatarImage
                          src={getUserAvatarUrl(post.author) || undefined}
                          alt={getUserDisplayName(post.author)}
                        />
                        <AvatarFallback className="text-[10px]" colorSeed={post.author.id}>
                          {getUserInitial(post.author)}
                        </AvatarFallback>
                      </Avatar>
                      <AdminUserLink userId={post.author.id} className="text-sm text-foreground">
                        {getUserDisplayName(post.author)}
                      </AdminUserLink>
                      <span className="text-muted-foreground">·</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-muted-foreground cursor-default">
                            {format(new Date(post.created), 'MMMM d, yyyy')}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          {format(new Date(post.created), "h:mm a")}
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Post Content */}
                    {(post.details || (post.imageURLs && post.imageURLs.length > 0)) && (
                      <div className="mt-4">
                        {post.details && (
                          <div className="prose prose-sm max-w-none text-sm leading-relaxed">
                            <MarkdownContent content={post.details} />
                          </div>
                        )}

                        {post.imageURLs && post.imageURLs.length > 0 && (
                          <div className={`grid grid-cols-2 gap-3 ${post.details ? 'mt-4' : ''}`}>
                            {post.imageURLs.map((url, index) => (
                              <S3Image
                                key={index}
                                src={url}
                                alt={`Post image ${index + 1}`}
                                className="rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => openLightbox(post.imageURLs!, index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile Details */}
              <div className="lg:hidden mb-6">
                <Collapsible open={mobileDetailsOpen} onOpenChange={setMobileDetailsOpen}>
                  <CollapsibleTrigger asChild>
                    <button className="w-full p-3 flex items-center justify-between text-left border rounded-lg hover:bg-muted/50 transition-colors">
                      <span className="text-sm font-medium">Details</span>
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${
                          mobileDetailsOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-3 space-y-4">
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground mb-1.5">Category</h4>
                        {post.category ? (
                          <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                              borderColor: post.category.color || '#3B82F6',
                              color: post.category.color || '#3B82F6',
                            }}
                          >
                            {post.category.name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Comments Block */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold">
                    Comments{' '}
                    {comments.length > 0 && (
                      <span className="text-muted-foreground font-normal">({comments.length})</span>
                    )}
                  </h2>
                  {comments.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Sort by</span>
                      <Select
                        value={commentSort}
                        onValueChange={(value) => setCommentSort(value as 'newest' | 'oldest')}
                      >
                        <SelectTrigger className="h-7 w-24 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="oldest">Oldest</SelectItem>
                          <SelectItem value="newest">Newest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Comment form */}
                <div className="mb-6" data-comment-form>
                  <CommentForm postId={post.id} placeholder="Leave a comment..." />
                </div>

                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No comments yet. Be the first to share your thoughts!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {sortedComments.map((comment) => (
                      <CommentItem
                        key={comment.id}
                        comment={comment}
                        postId={post.id}
                        currentUserId={user?.id}
                        isAdmin={user?.isAdmin}
                        onImageClick={openLightbox}
                        onDelete={setCommentToDelete}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-64 shrink-0 hidden lg:block">
              <PostDetailsSidebar
                postId={post.id}
                postTitle={post.title}
                boardId={post.board.id}
                status={status}
                category={
                  post.category
                    ? { ...post.category, color: post.category.color || '#3B82F6' }
                    : null
                }
                estimatedCompletionDate={post.estimatedCompletionDate}
              />
            </aside>
          </div>
        </main>

        {/* Mobile FAB */}
        <div className="fixed bottom-6 right-6 sm:hidden z-50">
          <Button
            onClick={focusCommentForm}
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg"
            aria-label="Add comment"
          >
            <MessageSquare className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Modals */}
        {post && (
          <EditPostModal
            post={{
              id: post.id,
              slug: post.slug,
              title: post.title,
              details: post.details,
              categoryId: post.category?.id,
              imageURLs: post.imageURLs,
            }}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            categories={boardData?.board?.categories || []}
          />
        )}

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          title="Delete Post"
          itemName={post.title}
          isDeleting={isDeleting}
        />

        <DeleteConfirmDialog
          isOpen={!!commentToDelete}
          onClose={() => setCommentToDelete(null)}
          onConfirm={handleDeleteComment}
          title="Delete comment"
          description="Are you sure you want to delete this comment? This action cannot be undone."
          isDeleting={deleteCommentMutation.isPending}
        />

        {lightboxImages.length > 0 && (
          <ImageLightbox
            images={lightboxImages}
            initialIndex={lightboxIndex}
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
